/**
 * 权限工具函数
 * 用于检查用户是否拥有指定权限
 */

/**
 * 获取当前用户的权限列表
 * 从 localStorage 或 initialState 中获取
 */
export function getCurrentPermissions(): string[] {
  try {
    const permissionsStr = localStorage.getItem('permissions');
    if (permissionsStr) {
      return JSON.parse(permissionsStr);
    }
  } catch (error) {
    console.error('获取权限列表失败:', error);
  }
  return [];
}

/**
 * 保存权限列表到 localStorage
 */
export function setCurrentPermissions(permissions: string[]): void {
  try {
    localStorage.setItem('permissions', JSON.stringify(permissions));
  } catch (error) {
    console.error('保存权限列表失败:', error);
  }
}

/**
 * 清除权限列表
 */
export function clearPermissions(): void {
  localStorage.removeItem('permissions');
}

/**
 * 检查用户是否拥有指定权限
 * @param permissionCode 权限编码，如 'admin:create', 'admin:edit' 等
 * @param permissions 权限列表，如果不传则从 localStorage 获取
 * @returns 是否拥有权限
 * 
 * @example
 * ```ts
 * // 检查单个权限
 * if (hasPermission('admin:create')) {
 *   // 显示创建按钮
 * }
 * 
 * // 检查多个权限（需要全部拥有）
 * if (hasPermission(['admin:create', 'admin:edit'])) {
 *   // 显示操作按钮
 * }
 * 
 * // 检查多个权限（拥有任意一个即可）
 * if (hasAnyPermission(['admin:create', 'admin:edit'])) {
 *   // 显示操作按钮
 * }
 * ```
 */
export function hasPermission(
  permissionCode: string | string[],
  permissions?: string[],
): boolean {
  const currentPermissions = permissions || getCurrentPermissions();

  // 如果没有权限列表，返回 false
  if (!currentPermissions || currentPermissions.length === 0) {
    return false;
  }

  // 如果传入的是数组，需要全部拥有
  if (Array.isArray(permissionCode)) {
    return permissionCode.every((code) => currentPermissions.includes(code));
  }

  // 单个权限码检查
  return currentPermissions.includes(permissionCode);
}

/**
 * 检查用户是否拥有任意一个权限
 * @param permissionCodes 权限编码数组
 * @param permissions 权限列表，如果不传则从 localStorage 获取
 * @returns 是否拥有任意一个权限
 * 
 * @example
 * ```ts
 * // 检查是否有创建或编辑权限
 * if (hasAnyPermission(['admin:create', 'admin:edit'])) {
 *   // 显示操作按钮
 * }
 * ```
 */
export function hasAnyPermission(
  permissionCodes: string[],
  permissions?: string[],
): boolean {
  const currentPermissions = permissions || getCurrentPermissions();
  
  // 如果没有权限列表，返回 false
  if (!currentPermissions || currentPermissions.length === 0) {
    return false;
  }

  // 检查是否有任意一个权限
  return permissionCodes.some((code) => currentPermissions.includes(code));
}

/**
 * 从权限树中提取所有权限编码（扁平化）
 * @param permissions 权限树
 * @returns 权限编码数组
 */
export function extractPermissionCodes(
  permissions: API.PermissionInfo[],
): string[] {
  const codes: string[] = [];

  function traverse(permissionList: API.PermissionInfo[]) {
    permissionList.forEach((permission) => {
      if (permission.permissionCode) {
        codes.push(permission.permissionCode);
      }
      if (permission.children && permission.children.length > 0) {
        traverse(permission.children);
      }
    });
  }

  traverse(permissions);
  return codes;
}

